import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";

interface Purchase {
  id: number; paidAt: string; refundUntil: string; creditedAllowance: number; bonusAllowance: number;
  refundedAt: string | null; reviewReason: string | null; slackCompletedAt: string | null;
  buyerNoticeAt: string | null; adminNoticeAt: string | null; shippedLocks: number; creditedLocks: number;
}
interface Account {
  userId: number; email: string; reserved: number; outstanding: number; shipped: number; credited: number; refundedShipped: number; refundedCredited: number; review: boolean; purchases: Purchase[];
}
const KEY = ["/api/admin/lifetime-fulfillment"];
export function LifetimeFulfillmentCard() {
  const { data, error } = useQuery<{ accounts: Account[]; totals: { reserved: number; outstanding: number } }>({ queryKey: KEY });
  return <Card className="space-y-4 p-4" data-testid="card-lifetime-fulfillment">
    <h4 className="font-semibold">Lifetime locks and private Slack onboarding</h4>
    <p className="text-sm text-muted-foreground">Payment-backed purchases only. Ship estimate: Q2 2027. $100 account credit per undelivered lock if not shipped by September 30, 2027; no cash value. Credits are recorded here only after actually issued, never issued by this screen. Onboard to Slack within one business day of payment.</p>
    {error ? <p role="alert">Could not load obligations.</p> : !data ? <p>Loading obligations…</p> : <>
      <p>{data.totals.reserved} reserved locks · {data.totals.outstanding} outstanding</p>
      {data.accounts.length === 0 && <p>No qualifying payment records yet. Historic grants are not inferred.</p>}
      {data.accounts.map((a) => <section className="space-y-3 rounded border p-3" key={a.userId}>
        <h5 className="font-semibold">{a.email} · Account {a.userId}</h5>
        <p>{a.reserved} reserved · {a.shipped} shipped · {a.credited} credited · {a.outstanding} outstanding {a.review && "· Review required"}</p>
        {(a.refundedShipped > 0 || a.refundedCredited > 0) && <p role="alert" className="text-sm">
          Refunded purchases have {a.refundedShipped} recorded shipped locks and {a.refundedCredited} recorded issued lock credits.
          Manual reconciliation is required; these do not satisfy retained purchases.
        </p>}
        {a.purchases.map((p) => <PurchaseRow key={p.id} purchase={p} />)}
      </section>)}
    </>}
  </Card>;
}
function PurchaseRow({ purchase: p }: { purchase: Purchase }) {
  const [shipped, setShipped] = useState(String(p.shippedLocks));
  const [credited, setCredited] = useState(String(p.creditedLocks));
  const update = useMutation({
    mutationFn: async (body: { slackCompleted?: boolean; shippedLocks?: number; creditedLocks?: number }) =>
      (await apiRequest("PATCH", `/api/admin/lifetime-fulfillment/${p.id}`, body)).json(),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: KEY }); },
  });
  const blocked = Boolean(p.refundedAt || p.reviewReason || update.isPending);
  return <div className="space-y-2 border-t pt-2">
    <p className="text-sm">Purchase {p.id} · {p.creditedAllowance} properties · {p.bonusAllowance > 0 ? "Paid bonus" : "Standard"} · Paid {new Date(p.paidAt).toLocaleString()} · Refund through {new Date(p.refundUntil).toLocaleString("en-US", { timeZone: "America/New_York" })} ET</p>
    <p className="text-sm">Buyer notice: {p.buyerNoticeAt ? "sent" : "pending retry"} · Travis notice: {p.adminNoticeAt ? "sent" : "pending retry"} · Slack: {p.slackCompletedAt ? "onboarded" : "manual onboarding due"}</p>
    {p.refundedAt && <p>Refunded: contribution void.</p>}
    {p.reviewReason && <p role="alert">{p.reviewReason}</p>}
    <div className="flex flex-wrap items-end gap-3">
      <Button size="sm" variant="outline" disabled={blocked} onClick={() => update.mutate({ slackCompleted: !p.slackCompletedAt })}>
        {p.slackCompletedAt ? "Mark Slack onboarding incomplete" : "Confirm Slack onboarding completed"}
      </Button>
      <label className="text-sm">Locks actually shipped<Input type="number" min={0} max={20} value={shipped} onChange={(e) => setShipped(e.target.value)} className="w-24" /></label>
      <label className="text-sm">Locks actually credited<Input type="number" min={0} max={20} value={credited} onChange={(e) => setCredited(e.target.value)} className="w-24" /></label>
      <Button size="sm" disabled={blocked || !/^\d+$/.test(shipped) || !/^\d+$/.test(credited)}
        onClick={() => update.mutate({ shippedLocks: Number(shipped), creditedLocks: Number(credited) })}>Record fulfillment</Button>
    </div>
    {update.error && <p role="alert">{parseErrorMessage(update.error, "Could not save fulfillment")}</p>}
  </div>;
}
